import {Component, OnInit} from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators
} from '@angular/forms';
import {UserRoleService} from '@services/user-role.service';
import {
    Permission,
    Section,
    UserRolePermissions
} from '@/common/interface/user.role.permission';
import {ToastrService} from 'ngx-toastr';
import {Observable} from 'rxjs';
import {LoaderService} from '@services/loader.service';

@Component({
    selector: 'app-user-permission',
    templateUrl: './user-permission.component.html',
    styleUrls: ['./user-permission.component.scss']
})
export class UserPermissionComponent implements OnInit {
    roleForm: FormGroup;
    sections: Section[] = [];
    permissions: Permission[] = [];
    public isAuthLoading = false;
    public isLoading$: Observable<boolean>;

    constructor(
        private fb: FormBuilder,
        private userRoleService: UserRoleService,
        private toastr: ToastrService,
        private loaderService: LoaderService
    ) {
        this.roleForm = this.fb.group({
            roleName: ['', Validators.required],
            dashboard: this.fb.group({
                sectionId: [null],
                sectionName: ['Dashboard'],
                permissions: this.fb.group({})
            }),
            catalog: this.fb.array([])
        });
        this.isLoading$ = this.loaderService.isLoading$;
    }

    ngOnInit(): void {
        this.loadSections();
        this.loadPermissions();
    }

    initializeForm() {
        this.setDashboardSectionId();
        this.initializeDashboardPermissions();
        this.initializeCatalogPermissions();
    }
    setDashboardSectionId() {
        const dashboardSection = this.sections.find(
            (section) => section.sectionName.toLowerCase() === 'dashboard'
        );
        if (dashboardSection) {
            this.roleForm
                .get('dashboard.sectionId')
                .setValue(dashboardSection.id);
        }
    }
    initializeDashboardPermissions() {
        const dashboardGroup = this.roleForm.get(
            'dashboard.permissions'
        ) as FormGroup;
        dashboardGroup.reset();

        this.permissions.forEach((permission) => {
            dashboardGroup.addControl(
                permission.permissionName,
                new FormControl(false)
            );
        });
    }

    initializeCatalogPermissions() {
        const catalogFormArray = this.roleForm.get('catalog') as FormArray;
        catalogFormArray.clear();

        this.sections
            .filter(
                (section) => section.sectionName.toLowerCase() !== 'dashboard'
            ) // Exclude the dashboard from the catalog
            .forEach((section) => {
                const sectionGroup = this.fb.group({
                    sectionName: [section.sectionName],
                    permissions: this.fb.group({})
                });

                this.permissions.forEach((permission) => {
                    (sectionGroup.get('permissions') as FormGroup).addControl(
                        permission.permissionName,
                        new FormControl(false)
                    );
                });

                catalogFormArray.push(sectionGroup);
            });
    }

    loadSections() {
        this.userRoleService
            .getSectionList()
            .subscribe((sections: Section[]) => {
                this.sections = sections;
                this.initializeForm();
            });
    }

    loadPermissions() {
        this.userRoleService
            .getPermissioList()
            .subscribe((permissions: Permission[]) => {
                this.permissions = permissions;
                this.initializeForm();
            });
    }

    get catalogFormArray(): FormArray {
        return this.roleForm.get('catalog') as FormArray;
    }

    onSubmit() {
        this.isAuthLoading = true;
        this.loaderService.showLoader();
        if (this.roleForm.invalid) {
            this.roleForm.markAllAsTouched(); // Mark all fields as touched to display validation messages
            this.isAuthLoading = false;
            this.loaderService.hideLoader();
            return;
        }
        const userRolePermissions: UserRolePermissions = {
            roleName: this.roleForm.value.roleName,
            permissions: []
        };
        const dashboardGroup = this.roleForm.get('dashboard') as FormGroup;

        const dashboardPermissions = this.roleForm.get(
            'dashboard.permissions'
        ).value;
        const dashboardSectionId = dashboardGroup.get('sectionId').value;
        for (const permission of this.permissions) {
            userRolePermissions.permissions.push({
                permissionId: permission.id,
                sectionId: dashboardSectionId,
                sectionName: null,
                isGranted: Boolean(
                    dashboardPermissions[permission.permissionName]
                )
            });
        }
        this.catalogFormArray.controls.forEach((sectionControl, index) => {
            const sectionPermissions = sectionControl.get('permissions').value;
            const sectionName = sectionControl.get('sectionName').value;
            // Find the corresponding section ID from the sections array
            const section = this.sections.find(
                (s) => s.sectionName === sectionName
            );
            const sectionId = section ? section.id : null;

            if (sectionId !== null) {
                for (const permission of this.permissions) {
                    userRolePermissions.permissions.push({
                        permissionId: permission.id,
                        sectionId: sectionId,
                        sectionName: sectionName, // Use the actual sectionId instead of the index
                        isGranted: Boolean(
                            sectionPermissions[permission.permissionName]
                        )
                    });
                }
            }
        });

        this.userRoleService
            .addUserRolePermissions(userRolePermissions)
            .subscribe({
                next: (response: any) => {
                    setTimeout(() => {
                        this.isAuthLoading = false;
                        this.loaderService.hideLoader();
                        this.toastr.success('Permissions saved successfully');
                    }, 2000);
                    this.roleForm.reset();
                    this.initializeForm();
                },
                error: (error) => {
                    console.error('Error occurred:', error);
                    setTimeout(() => {
                        this.isAuthLoading = false;
                        this.loaderService.hideLoader();
                        // Handle login error
                        this.toastr.error(error.error);
                    }, 2000);
                }
            });
    }
}
